import { Star, Quote } from "lucide-react";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";
import JudgemeReplica from "@/components/pdp/JudgemeReplica";

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={14} className={i < n ? "fill-riversol-blue text-riversol-blue" : "text-neutral-300"} />
    ))}
  </div>
);

/**
 * V3.1-only "What shoppers say" summary block. Renders above the two
 * customer-story cards when `reviews.summary` is present on the product
 * (currently hand-authored in `data/eyeRepairV31.js`). Two-column desktop
 * layout: editorial recap + theme pills on the left, 5-bar rating
 * distribution on the right. Stacks vertically on mobile.
 */
const ReviewsSummary = ({ summary, rating, reviewCount }) => (
  <Reveal>
    <div
      className="bg-white border border-neutral-200 p-8 lg:p-12 mb-8 lg:mb-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16"
      data-testid="reviews-summary"
    >
      {/* LEFT — editorial paragraph + top theme pills */}
      <div className="lg:col-span-7">
        <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-5">
          {summary.eyebrow}
        </div>
        <p
          className="text-[16px] lg:text-[18px] leading-[1.7] text-neutral-800 mb-8 max-w-xl"
          data-testid="reviews-summary-paragraph"
        >
          {summary.paragraph}
        </p>
        <div className="flex flex-wrap gap-2" data-testid="reviews-summary-themes">
          {summary.themes.map((t) => (
            <span
              key={t.label}
              className="inline-flex items-center gap-2 border border-neutral-300 rounded-full pl-3.5 pr-2.5 py-1.5 text-[12.5px] text-neutral-800 bg-white"
              data-testid={`reviews-summary-theme-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {t.label}
              <span className="font-mono text-[11px] text-neutral-500 tabular-nums">
                {t.count.toLocaleString()}
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* RIGHT — 5-bar rating distribution */}
      <div className="lg:col-span-5" data-testid="reviews-summary-distribution">
        <div className="flex items-baseline gap-3 mb-5">
          <span className="font-mono text-4xl lg:text-5xl text-neutral-900 leading-none">
            {rating}
          </span>
          <div className="flex flex-col">
            <Stars n={Math.round(rating)} />
            <span className="text-xs text-neutral-500 font-mono mt-1">
              {reviewCount.toLocaleString()} reviews
            </span>
          </div>
        </div>
        <ul className="space-y-2.5">
          {summary.distribution.map((d) => (
            <li
              key={d.stars}
              className="flex items-center gap-3"
              data-testid={`reviews-summary-bar-${d.stars}`}
            >
              <span className="font-mono text-[11px] text-neutral-500 tabular-nums w-6">
                {d.stars}★
              </span>
              <div
                className="flex-1 h-1.5 bg-neutral-200 rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={d.percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${d.stars} star reviews: ${d.percent} percent`}
              >
                <div
                  className="h-full bg-riversol-blue transition-[width] duration-700 ease-out"
                  style={{ width: `${d.percent}%` }}
                />
              </div>
              <span className="font-mono text-[11px] text-neutral-500 tabular-nums w-9 text-right">
                {d.percent}%
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </Reveal>
);

export const ReviewsSection = () => {
  const { reviews, rating, reviewCount, v31 } = useProduct();
  return (
    <section className="bg-riversol-paper py-20 lg:py-32" data-testid="reviews-section">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between mb-12 lg:mb-16 flex-wrap gap-6">
          <div>
            <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-6">
              {reviews.eyebrow}
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold text-neutral-900">
              {reviews.title}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-4xl lg:text-5xl text-neutral-900">{rating}</span>
            <div>
              <Stars n={Math.round(rating)} />
              <div className="text-sm text-neutral-500 mt-1">{reviewCount.toLocaleString()} verified reviews</div>
            </div>
          </div>
        </div>

        {/* V3.1: aggregate summary sits above the individual quote cards. */}
        {reviews.summary && (
          <ReviewsSummary
            summary={reviews.summary}
            rating={rating}
            reviewCount={reviewCount}
          />
        )}

        {/* V3.1 hides the two "Prominent customer story" placeholder
            cards — the ReviewsSummary above + the JudgemeReplica below
            already cover the aggregate + individual-voice social proof
            without the placeholder middle band. */}
        {!v31 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-12 lg:mb-16">
          {[0, 1].map((i) => (
            <Reveal key={i} delay={i * 0.12}>
              <figure className="bg-white border border-neutral-200 p-8 lg:p-12 h-full flex flex-col">
                <Quote size={32} className="text-riversol-blue/30 mb-6" />
                <div className="border border-dashed border-neutral-300 px-4 py-3 mb-6 inline-flex w-fit">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400">
                    Featured review — imported from Judge.me
                  </span>
                </div>
                <p className="text-lg lg:text-xl leading-relaxed text-neutral-700 flex-1">
                  Prominent customer story pulled from the live Judge.me review feed. Verified quote, skin-concern label and rating appear here — no review content is invented for this concept.
                </p>
                <figcaption className="mt-8 pt-6 border-t border-neutral-200 flex items-center justify-between">
                  <span className="text-sm text-neutral-500 font-mono">Verified buyer · placeholder</span>
                  <Stars n={5} />
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        )}

        <Reveal>
          {reviews.judgeme ? (
            <JudgemeReplica data={reviews.judgeme} rating={rating} />
          ) : (
            <div className="border border-dashed border-neutral-300 bg-white py-20 text-center" data-testid="judgeme-placeholder">
              <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-neutral-400 mb-3">
                Judge.me review widget
              </div>
              <p className="text-neutral-500 max-w-md mx-auto text-sm leading-relaxed">
                The complete Judge.me review system — filtering, ratings breakdown and all {reviewCount.toLocaleString()} reviews — renders here, unchanged from the live store.
              </p>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default ReviewsSection;
